import { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import {
  buyTournamentTicketApi,
  BuyTournamentTicketRequest,
  getSellerTournamentTicketLogList,
  getTournamentTicketListForSeller,
  SellerTournamentTicketLogResponse,
  TournamentTicketResponseForSeller,
} from "../../../api/tournament-ticket";
import { pubState } from "../../../recoil/pub.atom";

const LIMIT = 10;
const LOG_LIMIT = 10;

export const useTournamentTicketStore = () => {
  const pub = useRecoilValue(pubState);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [tournamentTicketList, setTournamentTicketList] = useState<
    TournamentTicketResponseForSeller[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [canBuyCount, setCanBuyCount] = useState(0);
  const [pubSeed, setPubSeed] = useState(0);
  const [pubCarrot, setPubCarrot] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [logPopupOpen, setLogPopupOpen] = useState(false);
  const [logList, setLogList] = useState<SellerTournamentTicketLogResponse[]>(
    [],
  );
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(0);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      if (query === searchQuery) return;
      setSearchQuery(query);
      setCurrentPage(1);
    },
    [setCurrentPage, searchQuery],
  );

  const getTournamentTicketList = useCallback(async () => {
    if (!pub) return;
    const res = await getTournamentTicketListForSeller({
      pubId: pub.id,
      page: currentPage,
      limit: LIMIT,
      q: searchQuery,
      type: "available",
    });
    setTournamentTicketList(res.list);
    setTotalCount(res.totalCount);
    setCanBuyCount(res.canBuyCount);
    setPubSeed(res.pubSeed);
    setPubCarrot(res.pubCarrot ?? 0);
    setTotalPages(Math.ceil(res.totalCount / LIMIT));
  }, [pub, currentPage, searchQuery]);

  const getLogList = useCallback(async () => {
    if (!pub) return;
    const res = await getSellerTournamentTicketLogList({
      pubId: pub.id,
      page: logPage,
      limit: LOG_LIMIT,
    });
    setLogList(res.list);
    setLogTotalPages(Math.ceil(res.totalCount / LOG_LIMIT));
  }, [pub, logPage]);

  useEffect(() => {
    getTournamentTicketList();
  }, [getTournamentTicketList]);

  useEffect(() => {
    if (!logPopupOpen) return;
    getLogList();
  }, [logPopupOpen, getLogList]);

  const openLogPopup = () => {
    setLogPage(1);
    setLogPopupOpen(true);
  };

  const buyTournamentTicket = useCallback(
    async (request: BuyTournamentTicketRequest) => {
      try {
        const res = await buyTournamentTicketApi(request);
        if (res.code === 200) {
          await getTournamentTicketList();
          return true;
        }
        alert(res.message || "교환에 실패했습니다.");
        return false;
      } catch (e: any) {
        alert(e?.message || "교환에 실패했습니다.");
        return false;
      }
    },
    [getTournamentTicketList],
  );

  return {
    currentPage,
    setCurrentPage,
    totalPages,
    tournamentTicketList,
    totalCount,
    canBuyCount,
    pubId: pub?.id || 0,
    pubSeed,
    pubCarrot,
    getTournamentTicketList,
    searchQuery,
    handleSearchQueryChange,
    buyTournamentTicket,
    logPopupOpen,
    setLogPopupOpen,
    openLogPopup,
    logList,
    logPage,
    setLogPage,
    logTotalPages,
  };
};
